import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../api";
import ScopePentestPanel from "./ScopePentestPanel";

/**
 * CISO Scope & Boundary (roadmap S1). Define a named engagement boundary — the in-scope domains,
 * cloud accounts, repos, vendors and systems — plus per-control applicability (mark a control N/A
 * with a justification). The scope's DOMAIN members become the Rules-of-Engagement allow-list that
 * the web/API pen-test engine is authorized to touch.
 */
// T-0266: CLOUD_ACCOUNT/REPO/VENDOR/SYSTEM are accepted by the API and rendered here if already
// present, but only DOMAIN is ever bridged into the ASM pipeline / pen-test RoE allow-list
// (ComplianceScopeService.addMember) — the other 4 just sit inert. Don't offer them as addable
// until the backend actually wires them up (see T-0266); TYPE_ICON keeps every type so existing
// non-DOMAIN members (added before this fix) still render correctly.
const ADDABLE_MEMBER_TYPES = ["DOMAIN"];
const TYPE_ICON = {
  DOMAIN: "fa-globe", CLOUD_ACCOUNT: "fa-cloud", REPO: "fa-code-branch",
  VENDOR: "fa-plug", SYSTEM: "fa-server",
};

export default function ScopeBoundaryPage() {
  const [scopes, setScopes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [applicability, setApplicability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [newName, setNewName] = useState("");
  const [mType, setMType] = useState("DOMAIN");
  const [mId, setMId] = useState("");
  const [mLabel, setMLabel] = useState("");
  const [naFw, setNaFw] = useState("");
  const [naCtrl, setNaCtrl] = useState("");
  const [naJust, setNaJust] = useState("");

  const loadScopes = useCallback(async () => {
    try {
      const r = await axiosInstance.get("/iasm/scope/scopes");
      setScopes(r.data || []);
    } catch (e) { setErr("Could not load scopes."); }
  }, []);

  const openScope = useCallback(async (s) => {
    setSelected(s); setErr(null); setLoading(true);
    try {
      const [m, a] = await Promise.all([
        axiosInstance.get(`/iasm/scope/scopes/${s.id}/members`),
        axiosInstance.get(`/iasm/scope/scopes/${s.id}/applicability`),
      ]);
      setMembers(m.data || []);
      setApplicability(a.data || []);
    } catch (e) { setErr("Could not load scope detail."); }
    setLoading(false);
  }, []);

  useEffect(() => { loadScopes(); }, [loadScopes]);

  const createScope = async () => {
    if (!newName.trim()) return;
    try {
      const r = await axiosInstance.post("/iasm/scope/scopes", { name: newName.trim() });
      setNewName("");
      await loadScopes();
      openScope(r.data);
    } catch (e) { setErr("Create failed."); }
  };

  const deleteScope = async (s) => {
    if (!window.confirm(`Delete scope "${s.name}"?`)) return;
    await axiosInstance.delete(`/iasm/scope/scopes/${s.id}`);
    if (selected && selected.id === s.id) { setSelected(null); setMembers([]); setApplicability([]); }
    loadScopes();
  };

  const addMember = async () => {
    if (!selected || !mId.trim()) return;
    try {
      await axiosInstance.post(`/iasm/scope/scopes/${selected.id}/members`,
        { memberType: mType, identifier: mId.trim(), label: mLabel.trim() || null });
      setMId(""); setMLabel("");
      openScope(selected);
    } catch (e) { setErr("Add member failed."); }
  };

  const removeMember = async (id) => {
    await axiosInstance.delete(`/iasm/scope/members/${id}`);
    openScope(selected);
  };

  const markNA = async () => {
    if (!selected || !naFw || !naCtrl || !naJust.trim()) { setErr("Framework, control and justification are required to exclude a control."); return; }
    try {
      await axiosInstance.put(`/iasm/scope/scopes/${selected.id}/applicability`,
        { frameworkId: Number(naFw), controlId: Number(naCtrl), applicable: false, justification: naJust.trim() });
      setNaFw(""); setNaCtrl(""); setNaJust("");
      openScope(selected);
    } catch (e) { setErr("Set applicability failed."); }
  };

  const domainAllowlist = members.filter(m => m.memberType === "DOMAIN")
    .flatMap(m => { const h = m.identifier.replace(/^https?:\/\//, "").replace(/\/.*$/, ""); return [h, `*.${h}`]; });
  const exclusions = applicability.filter(a => !a.applicable);

  const box = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 };
  const label = { fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 };
  const input = { padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 };
  const btn = { padding: "7px 14px", background: "#4f46e5", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>Scope &amp; Boundary</h2>
        <span style={{ color: "#64748b", fontSize: 13 }}>Define the audit / pen-test engagement boundary</span>
      </div>
      {err && <div style={{ ...box, borderColor: "#fca5a5", background: "#fef2f2", color: "#b91c1c" }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "start" }}>
        {/* ── Scope list ── */}
        <div style={box}>
          <div style={label}>Scopes</div>
          {scopes.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 8 }}>No scopes yet.</div>}
          {scopes.map(s => (
            <div key={s.id} onClick={() => openScope(s)}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                background: selected && selected.id === s.id ? "#eef2ff" : "transparent", marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteScope(s); }}
                style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }} title="Delete">
                <i className="fas fa-trash" />
              </button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input style={{ ...input, flex: 1 }} placeholder="New scope name" value={newName}
              onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && createScope()} />
            <button style={btn} onClick={createScope}>Add</button>
          </div>
        </div>

        {/* ── Scope detail ── */}
        <div>
          {!selected && <div style={{ ...box, color: "#94a3b8" }}>Select or create a scope to define its boundary.</div>}
          {selected && (
            <>
              {/* Members */}
              <div style={box}>
                <div style={label}>In-scope assets — {selected.name}</div>
                {loading && <div style={{ color: "#94a3b8" }}>Loading…</div>}
                {members.map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <i className={`fas ${TYPE_ICON[m.memberType] || "fa-circle"}`} style={{ color: "#6366f1", width: 16 }} />
                    <span style={{ fontFamily: "monospace" }}>{m.identifier}</span>
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>{m.memberType}{m.label ? ` · ${m.label}` : ""}</span>
                    <button onClick={() => removeMember(m.id)} style={{ marginLeft: "auto", background: "none", border: "none", color: "#cbd5e1", cursor: "pointer" }}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                  <select style={input} value={mType} onChange={e => setMType(e.target.value)}>
                    {ADDABLE_MEMBER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input style={{ ...input, flex: 1, minWidth: 200 }} placeholder="identifier (e.g. koorierinc.net, aws:1234…, repo url)"
                    value={mId} onChange={e => setMId(e.target.value)} onKeyDown={e => e.key === "Enter" && addMember()} />
                  <input style={{ ...input, width: 140 }} placeholder="label (optional)" value={mLabel} onChange={e => setMLabel(e.target.value)} />
                  <button style={btn} onClick={addMember}>Add asset</button>
                </div>
              </div>

              {/* Derived pen-test RoE */}
              <div style={box}>
                <div style={label}><i className="fas fa-shield-alt" /> Pen-test Rules-of-Engagement (derived)</div>
                {domainAllowlist.length === 0
                  ? <div style={{ color: "#94a3b8", fontSize: 13 }}>Add DOMAIN assets to authorize the web/API pen-test against them.</div>
                  : <div style={{ fontFamily: "monospace", fontSize: 13, color: "#334155" }}>{domainAllowlist.join("  ·  ")}</div>}
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>
                  Run: <code>POST /api/iasm/web-authz/run?scopeId={selected.id}</code> — the engine is authorized only for these hosts.
                </div>
              </div>

              {/* Applicability (SoA) */}
              <div style={box}>
                <div style={label}>Control applicability (Statement of Applicability)</div>
                {exclusions.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 8 }}>No controls excluded — all in scope.</div>}
                {exclusions.map(a => (
                  <div key={a.id} style={{ padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                    <b>Framework {a.frameworkId} · Control {a.controlId}</b> — <span style={{ color: "#b45309" }}>NOT APPLICABLE</span>
                    <div style={{ color: "#64748b" }}>{a.justification}</div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                  <input style={{ ...input, width: 120 }} placeholder="frameworkId" value={naFw} onChange={e => setNaFw(e.target.value)} />
                  <input style={{ ...input, width: 110 }} placeholder="controlId" value={naCtrl} onChange={e => setNaCtrl(e.target.value)} />
                  <input style={{ ...input, flex: 1, minWidth: 220 }} placeholder="justification (required to exclude)" value={naJust} onChange={e => setNaJust(e.target.value)} />
                  <button style={{ ...btn, background: "#b45309" }} onClick={markNA}>Mark N/A</button>
                </div>
              </div>

              {/* Pen-Test Engagement */}
              <ScopePentestPanel scopeId={selected.id} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
